import React from "react";
import {
  AssignmentOutlined,
  ContentCopyOutlined,
  FactCheckOutlined,
  KeyboardArrowRight,
  PlayArrowOutlined,
  TaskAltOutlined,
} from "@mui/icons-material";
import { Box, Button, Card, CardContent, Chip, Typography } from "@mui/material";
import "./WorkflowDefinitionCard.scss";

export type WorkflowStatus = "published" | "draft" | "archived";
export type WorkflowStageType = "form" | "approval" | "task";

export interface WorkflowDefinition {
  id: number;
  name: string;
  category: string;
  status: WorkflowStatus;
  description: string;
  version: number;
  stageCount: number;
  activeInstanceCount: number;
  editedOn: string;
  firstStageType: WorkflowStageType;
  isFlagship?: boolean;
}

interface WorkflowDefinitionCardProps {
  definition: WorkflowDefinition;
  onAction: (action: string, definition: WorkflowDefinition) => void;
}

const stageIcons: Record<WorkflowStageType, React.ReactElement> = {
  form: <AssignmentOutlined />,
  approval: <FactCheckOutlined />,
  task: <TaskAltOutlined />,
};

const WorkflowDefinitionCard: React.FC<WorkflowDefinitionCardProps> = ({
  definition,
  onAction,
}) => {
  const handleRun = () => onAction("Run", definition);
  const handleDuplicate = () => onAction("Duplicate", definition);
  const handleOpen = () => onAction("Open", definition);

  return (
    <Card component="article" className="workflow-card" variant="outlined">
      <CardContent className="workflow-card__content">
        {/* Tile and name share the top row; the pills sit on their own row below so the name
            still gets everything left of the card edge instead of ~110px. */}
        <Box className="workflow-card__header">
          <Box
            className={`workflow-card__stage-icon workflow-card__stage-icon--${definition.firstStageType}`}
            aria-label={`First stage: ${definition.firstStageType}`}
          >
            {stageIcons[definition.firstStageType]}
          </Box>

          <Box className="workflow-card__heading">
            <Typography component="h2" className="workflow-card__title">
              {/* Still clamped at two lines so an unusually long name can't stretch the grid row;
                  the full text stays reachable through the tooltip. */}
              <span className="workflow-card__title-text" title={definition.name}>
                {definition.name}
              </span>
            </Typography>
            <Typography className="workflow-card__category">
              {definition.category} workflow
            </Typography>
          </Box>
        </Box>

        <Box className="workflow-card__badges">
          {definition.isFlagship && (
            <Chip className="workflow-card__flagship" label="Flagship" size="small" />
          )}
          <Chip
            className={`workflow-card__status workflow-card__status--${definition.status}`}
            label={
              <>
                <span className="workflow-card__status-dot" aria-hidden="true" />
                {definition.status}
              </>
            }
            size="small"
          />
        </Box>

        <Typography className="workflow-card__description">{definition.description}</Typography>

        <Box className="workflow-card__meta" aria-label="Workflow details">
          <span>
            Version <strong>v{definition.version}</strong>
          </span>
          <span>
            Stages <strong>{definition.stageCount}</strong>
          </span>
          <span>
            Active <strong>{definition.activeInstanceCount}</strong>
          </span>
          <span>
            Edited <strong>{definition.editedOn}</strong>
          </span>
        </Box>

        <Box className="workflow-card__actions">
          <Button
            className="workflow-card__action"
            variant="outlined"
            color="secondary"
            size="small"
            startIcon={<PlayArrowOutlined />}
            onClick={handleRun}
          >
            Run
          </Button>
          <Button
            className="workflow-card__action"
            variant="outlined"
            color="secondary"
            size="small"
            startIcon={<ContentCopyOutlined />}
            onClick={handleDuplicate}
          >
            Duplicate
          </Button>
          <Button
            className="workflow-card__action workflow-card__action--open"
            variant="contained"
            size="small"
            endIcon={<KeyboardArrowRight />}
            onClick={handleOpen}
          >
            Open
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default WorkflowDefinitionCard;
